import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowFreeSpinIntro,
  setFreeSpinTransitionActive,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Device and orientation-specific size configuration for the free spin intro dialog
const INTRO_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
    },
  },
  DESKTOP: {
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
    },
  },
};

/**
 * FreeSpinIntroDialogController - Handles free spin intro animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 * Modeled on HoldAndWinIntroController
 */
class FreeSpinIntroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state (matching H&W intro pattern)
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;
    this.boneUpdateTicker = null;

    // Internal caches for performance optimization
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._fontSizeMultiplierCache = null;
    this._buttonYOffsetCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeIntroSpine();
  }

  /**
   * Get configuration based on current device and orientation
   */
  getConfig() {
    const { device, orientation } = this.dimensionsContext;

    // Return cached result if device/orientation unchanged
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._configCache;
    }

    // Map device string to config key
    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) {
      deviceKey = "MOBILE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      deviceKey = "TABLET";
    } else {
      deviceKey = "DESKTOP";
    }

    // Map orientation string to config key
    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    // Get device-specific config, fallback to DESKTOP LANDSCAPE
    const deviceConfig =
      INTRO_SIZE_CONFIG[deviceKey] || INTRO_SIZE_CONFIG.DESKTOP;
    const config =
      deviceConfig[orientationKey] ||
      deviceConfig.LANDSCAPE ||
      INTRO_SIZE_CONFIG.DESKTOP.LANDSCAPE;

    // Cache result
    this._configCache = config;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return config;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    // Get orientation-specific config
    const config = this.getConfig();

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply configuration multiplier for overall size
    scale *= config.spineScaleMultiplier;

    this.introSpine.scale.set(scale);
    // Position at dead center (parent container is already centered)
    this.introSpine.x = 0;
    this.introSpine.y = 0;
  }

  /**
   * Get device-specific font size multiplier for button text
   * Cached for performance - invalidated when device/orientation changes
   */
  getFontSizeMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._fontSizeMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._fontSizeMultiplierCache;
    }

    // All devices use the same multiplier (1.0)
    const multiplier = 1.0;

    // Cache result
    this._fontSizeMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._deviceScaleMultiplierCache = null;
      this._fontSizeMultiplierCache = null;
      this._buttonYOffsetCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // Always update spine positioning whenever dimensions change
    if (
      this.introSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the free spin intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 0.001;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.warmUpIntroSpine();

      this.initializeButton();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) {
        return null;
      }

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.introSpine.state.setAnimation(0, animations[0], false);
        this.introSpine.state.update(0.016);
        this.introSpine.skeleton.updateWorldTransform();
      }
      this.introSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.introSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.introSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize button and attach it to spine's "tap_to_contiune_text" bone
   * (bone name matches H&W spine - free spin spine may use a different name)
   *
   * The button is a child of the spine and follows the bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   */
  initializeButton() {
    if (!this.introSpine || !this.introSpine.skeleton) {
      return;
    }

    const buttonBone = this.introSpine.skeleton.findBone(
      "tap_to_contiune_text",
    );
    if (!buttonBone) {
      // Bone not found - button will not be visible but logic still works.
      // When the new spine with the correct bone is provided, it will attach automatically.
      return;
    }

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name =
      "free_spin_intro_continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Create text style (matching WinAnimationController exactly)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: 70, // Will be scaled by bone
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.introSpine.addChild(this.buttonContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Get device-specific Y offset for button positioning
   * Cached for performance - invalidated when device/orientation changes
   */
  getButtonYOffset() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._buttonYOffsetCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._buttonYOffsetCache;
    }

    let offset;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 335;
      } else {
        offset = 120;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 290;
      } else {
        offset = 185;
      }
    } else {
      offset = 80;
    }

    // Cache result
    this._buttonYOffsetCache = offset;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return offset;
  }

  /**
   * Sync button transform with spine's "tap_to_contiune_text" bone
   * Called every frame while spine is animating
   */
  updateButtonFromBone() {
    if (
      !this.introSpine ||
      !this.buttonContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const buttonBone = this.introSpine.skeleton.findBone(
      "tap_to_contiune_text",
    );
    if (!buttonBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY(),
    );
  }

  /**
   * Start syncing button with spine bone every frame
   * Uses PIXI.Ticker to update button transforms from bones
   */
  startBoneUpdateLoop() {
    if (!this.introSpine) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying) {
          this.updateButtonFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing button with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    this.buttonText.text = buttonText;
    this.buttonText.style.fontSize = 70;
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Restart the sequence from scratch - used when tab becomes active again after being hidden.
   */
  restartSequence() {
    if (this.isWaitingForClick) {
      return;
    }

    if (!this.isPlaying) {
      return;
    }

    // Clear all pending timeouts from the interrupted sequence
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Stop bone update loop from interrupted sequence
    this.stopBoneUpdateLoop();

    // Reset isPlaying so startSequence() guard doesn't block
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;

    // Restart cleanly
    this.startSequence();
  }

  /**
   * Start the intro animation sequence
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Play free_spin_intro sound when animation appears
      soundManager.play(SOUND_NAME.free_spin_intro);

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing
      if (
        this.introSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.introSpine.alpha = 1;
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      // Update button position immediately before showing to prevent flash
      this.updateButtonFromBone();

      // Start bone update loop to follow spine animation
      this.startBoneUpdateLoop();

      // SKIP PROTECTION: Block skipping for the first second (matching H&W pattern)
      // Show button after 1 second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play intro_in animation, then loop intro_idle animation
      this.introSpine.state.setAnimation(0, "intro_in", false);

      // Get intro_in animation duration
      const inDuration = this.getSpineAnimationDuration("intro_in");

      // After intro_in completes, start looping intro_idle
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.introSpine.state.setAnimation(0, "intro_idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.introSpine ||
      !this.introSpine.skeleton ||
      !this.introSpine.skeleton.data ||
      !this.introSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.introSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to close animation
   * Optimized debouncing - only 1 click processed
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.introSpine?.state) {
      return false;
    }

    // Block all clicks if debounce timeout exists
    if (this.debounceTimeout) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if waiting for click
    if (!this.isWaitingForClick) {
      return false;
    }

    // Create debounce timeout to block subsequent clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Close the animation
    this.playClosingAnimation();

    return true;
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop intro sound
    soundManager.stop(SOUND_NAME.free_spin_intro);

    this.introSpine.state.clearListeners();

    this.introSpine.state.setAnimation(0, "intro_out", false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "intro_out" && this.isPlaying) {
          // Start transition immediately when spine out animation finishes
          if (this.dispatch) {
            this.dispatch(setFreeSpinTransitionActive(true));
          }
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   */
  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.introSpine.alpha = 0.001;

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Wait for overlay fade out to complete before removing dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowFreeSpinIntro(false));
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    if (this.introSpine) {
      this.introSpine.state?.clearListeners();
      this.introSpine.state?.clearTracks();
      if (this.introSpine.state) this.introSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.introSpine)) {
        this.container.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }

    this.initialized = false;
  }
}

export default FreeSpinIntroDialogController;
